import type { PlanningResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { AttentionItem } from '@/components/pulse/attention-item';
import { AsOfPill } from '@/components/pulse/day';
import { PageHeader } from '@/components/pulse/page-header';
import { usePlanning } from '@/domains/people/api';
import { PeopleTabs } from '@/domains/people/people-page';
import { useShell } from '@/domains/shell/api';

/** People › Planning gaps (PPL-4), built from the prototype's `viewGaps()`. A current-state page: as of now. */
export function PlanningPage(): React.ReactElement {
  const page = usePlanning();
  const shell = useShell();
  return (
    <>
      <PageHeader
        eyebrow="People · planning"
        title="Planning gaps"
        sub="Where the workload numbers are weakest: no due dates, overdue work, old assignments and stories nobody owns."
        right={<AsOfPill label={shell.data?.freshness.asOfLabel ?? null} />}
      />
      <PeopleTabs active="planning" />
      {page.data !== undefined ? <PlanningView data={page.data} /> : null}
    </>
  );
}

const muted = { fontSize: 13, color: 'var(--muted)' } as const;

export function PlanningView({ data }: { readonly data: PlanningResponse }): React.ReactElement {
  const scale = Math.max(0, ...data.noDueDate.map((row) => row.open));
  const none = (text: string) => <span style={{ color: 'var(--ink-2)' }}>{data.recorded ? text : 'Not collected yet'}</span>;
  return (
    <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 380px' }}>
      <section className="card pad stack" style={{ gap: 12 }}>
        <h2>Open tasks with no due date</h2>
        <span style={muted}>Without dates, &quot;next up&quot; falls back to stage and priority.</span>
        {data.noDueDate.length === 0 ? none('Nobody has open Teamwork tasks.') : null}
        {data.noDueDate.map((row) => (
          <div key={row.userId} className="wl-row" style={{ gridTemplateColumns: '120px minmax(0,1fr) 90px' }}>
            <Link to={row.href} style={{ fontWeight: 600, color: 'var(--ink)', textDecoration: 'none' }}>
              {row.displayName}
            </Link>
            <span
              className="wl-bar"
              role="img"
              aria-label={`${row.withoutDueDate} of ${row.open} open tasks have no due date`}
              style={{ width: `${scale === 0 ? 0 : ((row.open / scale) * 100).toFixed(1)}%` }}
            >
              <i style={{ width: `${((row.withoutDueDate / row.open) * 100).toFixed(1)}%`, background: 'var(--s-draft)' }} />
              <i style={{ width: `${(((row.open - row.withoutDueDate) / row.open) * 100).toFixed(1)}%`, background: 'var(--s-ready)' }} />
            </span>
            <span className="r num">
              {row.withoutDueDate} of {row.open}
            </span>
          </div>
        ))}
        <div className="legend">
          <span>
            <i className="sw" style={{ background: 'var(--s-draft)' }} />
            No due date
          </span>
          <span>
            <i className="sw" style={{ background: 'var(--s-ready)' }} />
            Has a due date
          </span>
        </div>
      </section>
      <aside className="stack" style={{ gap: 16 }}>
        <section className="card pad stack" style={{ gap: 10 }}>
          <h2>Overdue</h2>
          {data.overdue.length === 0 && data.dueToday.length === 0 ? none('Nothing overdue or due today.') : null}
          {[...data.overdue, ...data.dueToday].map((row) => (
            <AttentionItem key={`${row.ref}${row.url}`} row={row} />
          ))}
        </section>
        <section className="card pad stack" style={{ gap: 10 }}>
          <h2>Old assignments</h2>
          {data.oldAssignments.length === 0 ? none('Nothing assigned has gone 90 days without activity.') : null}
          {data.oldAssignments.map((row) => (
            <div key={row.userId} className="stack" style={{ gap: 2 }}>
              <span style={{ fontWeight: 600 }}>{row.displayName}</span>
              <span style={{ color: 'var(--ink-2)' }}>{row.line}</span>
            </div>
          ))}
        </section>
        {data.unassignedStories.length === 0 ? (
          <section className="card pad stack" style={{ gap: 8 }}>
            <h2>Stories nobody owns</h2>
            {none('Every open story has an owner.')}
          </section>
        ) : (
          data.unassignedStories.map((row) => (
            <section key={row.productName} className="card pad stack" style={{ gap: 8 }}>
              <h2>{row.productName} stories nobody owns</h2>
              <span className="kpi-value num">{row.total}</span>
              <span style={{ color: 'var(--ink-2)' }}>
                {row.line} {row.total === 1 ? 'has' : 'have'} no <span className="mono">assigned_to</span>.
              </span>
            </section>
          ))
        )}
      </aside>
    </div>
  );
}
